import { Component, ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@diversioteam/diversio-ds'
import * as Sentry from '@sentry/react'

import { IErrorBoundaryProps, IErrorBoundaryState } from './ErrorBoundary.types'
import * as S from './ErrorBoundary.styles'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { getCurrentFeature, getCurrentFeatureArea } from '@/lib/sentry'
import { store } from '@/store/store'

class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
    public state: IErrorBoundaryState = {
        hasError: false,
        error: null,
    }

    public static getDerivedStateFromError(error: Error): IErrorBoundaryState {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (this.props.onError) {
            this.props.onError(error, errorInfo)
        }

        Sentry.withScope((scope) => {
            const state = store.getState()
            const role = state.auth.user?.role?.role ?? 'unknown'

            scope.setTag('user_role', role)
            scope.setTag('feature_area', getCurrentFeatureArea())
            scope.setContext('redux_state', {
                isAuthenticated: state.auth.isAuthenticated,
                organizationId: state.auth.organization?.uuid,
            })
            scope.setContext('error_boundary', {
                componentStack: errorInfo.componentStack,
                currentFeature: getCurrentFeature(),
            })
            Sentry.captureException(error)
        })
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null })
    }

    private handleReload = () => {
        window.location.reload()
    }

    public render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default error UI
            return (
                <S.Container>
                    <Card style={{ width: '100%', maxWidth: '28rem' }}>
                        <CardHeader style={{ textAlign: 'center' }}>
                            <S.IconWrapper>
                                <AlertTriangle
                                    style={{
                                        height: '1.5rem',
                                        width: '1.5rem',
                                        color: '#ef4444',
                                    }}
                                />
                            </S.IconWrapper>
                            <CardTitle>Something went wrong</CardTitle>
                            <CardDescription>
                                We encountered an unexpected error. Please try refreshing the page.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <S.ErrorContent>
                                {process.env.NODE_ENV === 'development' && this.state.error && (
                                    <S.ErrorMessageBox>
                                        <S.ErrorMessage>
                                            {/* SECURITY: Sanitize error messages to prevent information disclosure */}
                                            {this.state.error.message
                                                .replace(/\b[\w._%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, '[email]')
                                                .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[ssn]')
                                                .replace(/token[\s=:]+[\w-]+/gi, 'token=[redacted]')
                                                .replace(/password[\s=:]+\S+/gi, 'password=[redacted]')
                                                .replace(/api[_-]?key[\s=:]+\S+/gi, 'api_key=[redacted]')}
                                        </S.ErrorMessage>
                                    </S.ErrorMessageBox>
                                )}
                                <S.ButtonGroup>
                                    <Button onClick={this.handleReset} color="transparent" style={{ flex: 1 }}>
                                        Try Again
                                    </Button>
                                    <Button
                                        onClick={this.handleReload}
                                        color="primary"
                                        style={{ flex: 1 }}
                                        leftIcon={<RefreshCw className="h-4 w-4" />}
                                    >
                                        Reload Page
                                    </Button>
                                </S.ButtonGroup>
                            </S.ErrorContent>
                        </CardContent>
                    </Card>
                </S.Container>
            )
        }

        return this.props.children
    }
}

export { ErrorBoundary }
